import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { BLOG_POSTS } from "./posts";

// Set document meta tags without a helmet library
const useDocumentMeta = ({ title, description, canonicalPath }) => {
  useEffect(() => {
    if (title) document.title = title;

    const setMeta = (selector, attrs) => {
      let el = document.querySelector(selector);
      if (!el) {
        el = document.createElement(attrs.tag);
        Object.entries(attrs.props).forEach(([k, v]) => el.setAttribute(k, v));
        document.head.appendChild(el);
      }
      if (attrs.content !== undefined) el.setAttribute("content", attrs.content);
      if (attrs.href !== undefined) el.setAttribute("href", attrs.href);
    };

    if (description) {
      setMeta('meta[name="description"]', {
        tag: "meta",
        props: { name: "description" },
        content: description,
      });
      setMeta('meta[property="og:description"]', {
        tag: "meta",
        props: { property: "og:description" },
        content: description,
      });
    }
    if (title) {
      setMeta('meta[property="og:title"]', {
        tag: "meta",
        props: { property: "og:title" },
        content: title,
      });
    }
    if (canonicalPath) {
      const href = `https://passionpilates.au${canonicalPath}`;
      setMeta('link[rel="canonical"]', {
        tag: "link",
        props: { rel: "canonical" },
        href,
      });
      setMeta('meta[property="og:url"]', {
        tag: "meta",
        props: { property: "og:url" },
        content: href,
      });
    }
  }, [title, description, canonicalPath]);
};

export default function BlogIndex() {
  useDocumentMeta({
    title: "Passion Pilates Blog — Reformer Pilates Tips, Guides & News",
    description:
      "The Passion Pilates Chadstone blog — reformer Pilates guides for beginners, postnatal Pilates advice, studio news and expert tips from our Melbourne teachers.",
    canonicalPath: "/blog",
  });

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#E8DCC4]" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
      {/* Header */}
      <header className="border-b border-[#E8DCC4]/15">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-10 flex items-center justify-between">
          <Link to="/" className="font-sans text-[11px] tracking-[0.3em] uppercase font-semibold text-[#C97654] hover:text-[#E8DCC4] transition-colors">
            ← Passion Pilates · Chadstone
          </Link>
          <p className="font-sans text-[11px] tracking-[0.3em] uppercase text-[#E8DCC4]/50">Journal</p>
        </div>
      </header>

      {/* Editorial hero */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-16 pb-14">
        <p className="font-sans text-[11px] tracking-[0.38em] uppercase font-semibold text-[#C97654] mb-4">Journal</p>
        <h1 className="text-[#FBF6EC] font-normal leading-[0.98] tracking-[-1.5px] text-[54px] md:text-[76px]">
          Reformer notes,<br/>
          <em className="italic text-[#C97654]">by us.</em>
        </h1>
        <div className="h-px bg-[#FBF6EC]/25 mt-10 max-w-2xl"></div>
        <p className="font-sans mt-6 max-w-2xl text-[16px] md:text-[17px] leading-[1.65] text-[#E8DCC4]/80">
          Studio news, honest reformer Pilates guides, and everything we tell our friends when they ask "where should I start?".
        </p>
      </section>

      {/* Posts list */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-24">
        <div className="grid gap-12">
          {BLOG_POSTS.map((post) => (
            <article key={post.slug} className="grid md:grid-cols-[280px_1fr] gap-8 border-t border-[#E8DCC4]/15 pt-10 first:border-t-0 first:pt-0">
              <Link to={`/blog/${post.slug}`} className="block group">
                {post.heroImage && (
                  <div className="overflow-hidden bg-[#1a1a1a]">
                    <img
                      src={post.heroImage}
                      alt={post.title}
                      className="w-full h-52 md:h-40 object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                    />
                  </div>
                )}
              </Link>
              <div>
                <p className="font-sans text-[10px] tracking-[0.3em] uppercase text-[#C97654] mb-3">
                  {new Date(post.date).toLocaleDateString("en-AU", { day: "numeric", month: "long", year: "numeric" })} · {post.readMinutes} min read
                </p>
                <h2 className="text-[#FBF6EC] text-[26px] md:text-[30px] leading-tight font-normal tracking-[-0.5px] mb-3">
                  <Link to={`/blog/${post.slug}`} className="hover:text-[#C97654] transition-colors">
                    {post.title}
                  </Link>
                </h2>
                <p className="font-sans text-[15px] leading-[1.6] text-[#E8DCC4]/75 mb-5">
                  {post.excerpt}
                </p>
                <Link
                  to={`/blog/${post.slug}`}
                  className="inline-block font-sans font-bold text-[12px] tracking-[0.25em] uppercase text-[#FBF6EC] border-b-2 border-[#C97654] pb-[3px] hover:text-[#C97654] transition-colors"
                >
                  Read the post →
                </Link>
              </div>
            </article>
          ))}
        </div>
      </section>

      {/* Footer strip */}
      <footer className="border-t border-[#E8DCC4]/15 py-10">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <p className="font-sans text-[12px] tracking-[0.2em] uppercase text-[#E8DCC4]/55">Passion Pilates · Chadstone</p>
          <Link to="/" className="font-sans text-[12px] tracking-[0.2em] uppercase font-bold text-[#E8DCC4] border-b-2 border-[#C97654] pb-[2px] hover:text-[#C97654] transition-colors">
            Back to home →
          </Link>
        </div>
      </footer>
    </div>
  );
}
